const DEFAULT_BASE = '/streams';

function normalizeBase(base?: string): string {
  return (base ?? DEFAULT_BASE).replace(/\/$/, '');
}

/**
 * WebSocket URL for go2rtc's WebRTC client (`/api/ws`). The browser
 * exchanges SDP via `webrtc/offer` + `webrtc/answer` text frames.
 *
 * Always derives the protocol + host from the current page so a single
 * build artifact works on http://localhost and https://prod without a
 * baked-in URL.
 */
export function streamWsUrl(name: string, base?: string): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const root = normalizeBase(base);
  const url = root.startsWith('http')
    ? root.replace(/^http/, 'ws')
    : `${proto}//${window.location.host}${root}`;
  return `${url}/api/ws?src=${encodeURIComponent(name)}`;
}

/**
 * WebSocket URL for go2rtc's MSE-LD endpoint.
 *
 * Codec selection happens via the FIRST WS message from the client, not
 * via query params — go2rtc's `internal/mp4/ws.go` registers the MSE
 * handler under the message-type key `"mse"` and only starts streaming
 * once it receives `{type: "mse", value: "<codec list>"}`. Earlier
 * iterations of this code passed `&mse&mp4=hvc1,avc1` in the URL on the
 * assumption that go2rtc would auto-start MSE; it doesn't, the server
 * just sits there silent and the handshake times out. See
 * `mseHandshakeMessage()` below for the codec list we actually send.
 */
export function streamMseUrl(name: string, base?: string): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const root = normalizeBase(base);
  const url = root.startsWith('http')
    ? root.replace(/^http/, 'ws')
    : `${proto}//${window.location.host}${root}`;
  return `${url}/api/ws?src=${encodeURIComponent(name)}`;
}

/**
 * Build the JSON handshake the client sends on `ws.onopen` to kick off
 * MSE-LD streaming. Mirrors go2rtc's own `www/video-rtc.js` codec list
 * but keeps audio out: UniFi cameras typically emit PCMU (G.711 µ-law);
 * asking for `mp4a` / `opus` forces go2rtc to drop audio or spin up an
 * audio transcode, and on cold camera connects the muxer has been
 * observed to stall on that decision long enough to time out the
 * handshake (1.9.14 + UniFi G5). Shooting-range cameras don't need
 * audio anyway.
 *
 * The browser-side `MediaSource.isTypeSupported` filter strips codecs
 * the platform decoder won't accept, so go2rtc only sees what'll
 * actually play here. go2rtc matches by codec family (avc1 → H.264,
 * hvc1 → H.265), not exact profile-level — sending one representative
 * profile per codec family is enough.
 */
const MSE_VIDEO_CODECS = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'hvc1.1.6.L153.B0',
];

export function mseHandshakeMessage(): string {
  if (typeof MediaSource === 'undefined') {
    return JSON.stringify({ type: 'mse', value: 'avc1.640029' });
  }
  const supported = MSE_VIDEO_CODECS.filter((codec) =>
    MediaSource.isTypeSupported(`video/mp4; codecs="${codec}"`),
  );
  // If nothing matched (very old browser), still send the full list —
  // go2rtc will pick the closest match server-side. Empty string would
  // be treated as "use defaults" and pull audio back in.
  const value = supported.length > 0 ? supported.join(',') : MSE_VIDEO_CODECS.join(',');
  return JSON.stringify({ type: 'mse', value });
}

/** HTML5 video src URL for go2rtc's HLS player (~5 s latency). */
export function streamHlsUrl(name: string, base?: string): string {
  return `${normalizeBase(base)}/api/stream.m3u8?src=${encodeURIComponent(name)}`;
}

/** go2rtc's stream.html embed — easiest preview path. */
export function streamEmbedUrl(name: string, base?: string): string {
  return `${normalizeBase(base)}/stream.html?src=${encodeURIComponent(name)}&mode=mse,webrtc`;
}

// ── WebRTC signalling (go2rtc `/api/ws`) ────────────────────────────────
// go2rtc reads every text frame as JSON `{type, value}` and closes the
// socket on anything else (1.9.x internal/webrtc asyncHandler):
//   → {"type":"webrtc/offer","value":"<sdp>"}
//   ← {"type":"webrtc/answer","value":"<sdp>"}
//   ← {"type":"webrtc/candidate","value":"candidate:…"}   (after the answer)
//   → {"type":"webrtc/candidate","value":"candidate:…"}   (ours)
//   ← {"type":"error","value":"…"}
// Same protocol as the native tile (apps/mobile …/camera/liveCameraModel.ts).

export function webrtcOfferMessage(sdp: string): string {
  return JSON.stringify({ type: 'webrtc/offer', value: sdp });
}

/** `candidate` = RTCIceCandidate.candidate ('' = end of candidates). */
export function webrtcCandidateMessage(candidate: string): string {
  return JSON.stringify({ type: 'webrtc/candidate', value: candidate });
}

export type WebrtcSignal =
  | { kind: 'answer'; sdp: string }
  | { kind: 'candidate'; candidate: string }
  | { kind: 'error'; message: string };

/** Parse one inbound frame; null for binary / unknown / irrelevant frames. */
export function parseWebrtcSignal(data: unknown): WebrtcSignal | null {
  if (typeof data !== 'string') return null;
  let msg: unknown;
  try {
    msg = JSON.parse(data);
  } catch {
    return null;
  }
  if (!msg || typeof msg !== 'object') return null;
  const { type, value } = msg as { type?: unknown; value?: unknown };
  if (type === 'webrtc/answer' && typeof value === 'string' && value) return { kind: 'answer', sdp: value };
  if (type === 'webrtc/candidate' && typeof value === 'string' && value) {
    return { kind: 'candidate', candidate: value };
  }
  if (type === 'error') return { kind: 'error', message: typeof value === 'string' ? value : 'unknown error' };
  return null;
}
